import { useState } from 'react';
// @mui
import {
  Card,
  Container,
  Typography,
  Grid,
  Box,
  CardContent,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Paper,
  Stack,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Assessment,
  Business,
  Category,
  Person,
  QrCode,
  Build,
  Analytics,
  TrendingUp,
  Receipt,
  Engineering,
  Store,
  Assignment,
  Search,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { Page } from '../../../components';
import {
  BarcodeReportForm,
  BranchReportForm,
  CategoryReportForm,
  MaintenanceAssetReport,
  MaintenanceBranchReport,
  MaintenanceCategoryExpensesReport,
  MaintenanceSupplierExpensesReport,
  ManitenanceOverviewReport,
  ManitenanceSupplierReport,
  StaffReportForm,
  AuditedAssetsReport,
  UnAuditedAssetsReport,
} from '../components';
import { generateAssetMasterReport } from '../components/MasterAssetReport';
import { generateGapAnalysisReport } from '../components/GapAnalysisReport';

// ----------------------------------------------------------------------

const ReportCategory = ({ title, icon, color = 'primary', children, open, onToggle, description }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: `1px solid ${alpha(theme.palette[color].main, 0.12)}`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: alpha(theme.palette[color].main, 0.24),
          boxShadow: theme.customShadows.z8,
        },
      }}
    >
      <ListItemButton
        onClick={onToggle}
        sx={{
          py: 2.5,
          px: 3,
          bgcolor: alpha(theme.palette[color].main, 0.04),
          '&:hover': {
            bgcolor: alpha(theme.palette[color].main, 0.08),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette[color].main, 0.12),
              color: theme.palette[color].main,
            }}
          >
            {icon}
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[color].dark }}>
              {title}
            </Typography>
          }
          secondary={
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {description}
            </Typography>
          }
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ py: 1 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

const ReportItem = ({ title, description, icon, onClick, color = 'primary', badge }) => {
  const theme = useTheme();

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        mx: 2,
        mb: 1,
        py: 2,
        px: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: theme.palette[color].main,
          bgcolor: alpha(theme.palette[color].main, 0.04),
          transform: 'translateX(4px)',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette[color].main, 0.08),
            color: theme.palette[color].main,
          }}
        >
          {icon}
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
            {badge && (
              <Chip label={badge} size="small" color={color} variant="soft" sx={{ height: 20, fontSize: '0.75rem' }} />
            )}
          </Stack>
        }
        secondary={
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {description}
          </Typography>
        }
      />
    </ListItemButton>
  );
};

export default function Reports() {
  const theme = useTheme();
  const [openAsset, setOpenAsset] = useState(false);
  const [openAudit, setOpenAudit] = useState(false);
  const [openTagging, setOpenTagging] = useState(false);
  const [openMaintenance, setOpenMaintenance] = useState(false);
  const [openGapAnalysis, setOpenGapAnalysis] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('');

  const handleMenuClick = (option) => {
    setSelectedMenu(option);
  };

  let formComponent;
  if (selectedMenu === 'branch') {
    formComponent = <BranchReportForm />;
  } else if (selectedMenu === 'category') {
    formComponent = <CategoryReportForm />;
  } else if (selectedMenu === 'manager') {
    formComponent = <StaffReportForm />;
  } else if (selectedMenu === 'barcodeNumbers') {
    formComponent = <BarcodeReportForm />;
  } else if (selectedMenu === 'repairOverview') {
    formComponent = <ManitenanceOverviewReport />;
  } else if (selectedMenu === 'repairPerSupplier') {
    formComponent = <ManitenanceSupplierReport />;
  } else if (selectedMenu === 'repairPerBranch') {
    formComponent = <MaintenanceBranchReport />;
  } else if (selectedMenu === 'repairAssetsReport') {
    formComponent = <MaintenanceAssetReport />;
  } else if (selectedMenu === 'repairCategoryExpenses') {
    formComponent = <MaintenanceCategoryExpensesReport />;
  } else if (selectedMenu === 'repairSupplierExpenses') {
    formComponent = <MaintenanceSupplierExpensesReport />;
  } else if (selectedMenu === 'auditedAssets') {
    formComponent = <AuditedAssetsReport />;
  } else if (selectedMenu === 'unauditedAssets') {
    formComponent = <UnAuditedAssetsReport />;
  }

  const reportCategories = [
    {
      title: 'Asset Reports',
      icon: <Assessment />,
      color: 'primary',
      description: 'Generate comprehensive asset reports by branch, category, and manager',
      open: openAsset,
      onToggle: () => setOpenAsset(!openAsset),
      items: [
        {
          title: 'Asset Per Branch',
          description: 'View assets distributed across different branches',
          icon: <Business />,
          onClick: () => handleMenuClick('branch'),
        },
        {
          title: 'Asset Per Category',
          description: 'Analyze assets grouped by categories',
          icon: <Category />,
          onClick: () => handleMenuClick('category'),
        },
        {
          title: 'Asset Per Custodian',
          description: 'Track assets assigned to specific staff',
          icon: <Person />,
          onClick: () => handleMenuClick('manager'),
        },
        {
          title: 'Asset Master Report',
          description: 'Complete asset inventory and details',
          icon: <Assignment />,
          onClick: () => generateAssetMasterReport(),
          badge: 'Master',
        },
      ],
    },
    {
      title: 'Audit Reports',
      icon: <Search />,
      color: 'info',
      description: 'Monitor audit status and compliance across branches',
      open: openAudit,
      onToggle: () => setOpenAudit(!openAudit),
      items: [
        {
          title: 'Audited Assets',
          description: 'Audited reports',
          icon: <Business />,
          onClick: () => handleMenuClick('auditedAssets'),
        },
        {
          title: 'UnAudited Assets',
          description: 'Identify assets that require audit attention',
          icon: <Warning />,
          onClick: () => handleMenuClick('unauditedAssets'),
          badge: 'Pending',
        },
      ],
    },
    {
      title: 'Asset Tagging Reports',
      icon: <QrCode />,
      color: 'secondary',
      description: 'Generate reports for asset identification and tracking',
      open: openTagging,
      onToggle: () => setOpenTagging(!openTagging),
      items: [
        {
          title: 'Barcode Numbers',
          description: 'Export barcode information for asset tracking',
          icon: <QrCode />,
          onClick: () => handleMenuClick('barcodeNumbers'),
        },
      ],
    },
    {
      title: 'Maintenance Reports',
      icon: <Build />,
      color: 'warning',
      description: 'Comprehensive maintenance and repair analysis',
      open: openMaintenance,
      onToggle: () => setOpenMaintenance(!openMaintenance),
      items: [
        {
          title: 'Overview Report',
          description: 'High-level maintenance summary and statistics',
          icon: <Analytics />,
          onClick: () => handleMenuClick('repairOverview'),
        },
        {
          title: 'Repairs Per Supplier',
          description: 'Maintenance work performed by each supplier',
          icon: <Store />,
          onClick: () => handleMenuClick('repairPerSupplier'),
        },
        {
          title: 'Repairs Per Location',
          description: 'Maintenance activities across different locations',
          icon: <Business />,
          onClick: () => handleMenuClick('repairPerBranch'),
        },
        {
          title: 'Assets Report',
          description: 'Detailed maintenance history for specific assets',
          icon: <Engineering />,
          onClick: () => handleMenuClick('repairAssetsReport'),
        },
        {
          title: 'Repair Expenses Per Asset Class',
          description: 'Cost analysis by asset category',
          icon: <Receipt />,
          onClick: () => handleMenuClick('repairCategoryExpenses'),
        },
        {
          title: 'Repair Expenses Per Supplier',
          description: 'Cost breakdown by maintenance provider',
          icon: <TrendingUp />,
          onClick: () => handleMenuClick('repairSupplierExpenses'),
        },
      ],
    },
    {
      title: 'Gap Analysis Report',
      icon: <Analytics />,
      color: 'success',
      description: 'Identify gaps and opportunities in asset management',
      open: openGapAnalysis,
      onToggle: () => setOpenGapAnalysis(!openGapAnalysis),
      items: [
        {
          title: 'Assets Gap Analysis Report',
          description: 'Comprehensive gap analysis for asset optimization',
          icon: <CheckCircle />,
          onClick: () => generateGapAnalysisReport(),
          badge: 'Analysis',
        },
      ],
    },
  ];

  return (
    <Page title="Reports">
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
            Reports Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Generate comprehensive reports to analyze your asset management data
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={4}>
            <Card
              sx={{
                height: 'fit-content',
                position: 'sticky',
                top: 24,
                boxShadow: theme.customShadows.z8,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Report Categories
                </Typography>

                <Stack spacing={1}>
                  {reportCategories.map((category, index) => (
                    <ReportCategory
                      key={index}
                      title={category.title}
                      icon={category.icon}
                      color={category.color}
                      description={category.description}
                      open={category.open}
                      onToggle={category.onToggle}
                    >
                      {category.items.map((item, itemIndex) => (
                        <ReportItem
                          key={itemIndex}
                          title={item.title}
                          description={item.description}
                          icon={item.icon}
                          onClick={item.onClick}
                          color={category.color}
                          badge={item.badge}
                        />
                      ))}
                    </ReportCategory>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={8}>
            <Card
              sx={{
                minHeight: 600,
                boxShadow: theme.customShadows.z8,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {formComponent || (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 500,
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        mb: 3,
                      }}
                    >
                      <Analytics sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                      Select a Report
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 400 }}>
                      Choose a report category from the sidebar to generate detailed insights and analytics for your
                      asset management system.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
